
import React, { useState } from "react";
import { DocumentProps } from "@/hooks/admin/identity-docs/useSubmittedDocuments";
import { DocumentApprovalDialogComponent } from "../actions/DocumentApprovalDialogComponent";
import { DocumentRejectionDialog } from "../DocumentRejectionDialog";
import { useDocumentVerificationHandlers } from "../actions/useDocumentVerificationHandlers";
import { Button } from "@/components/ui/button";
import { CheckCircle2, X, Star } from "lucide-react";

interface DocumentPreviewActionsProps {
  document: DocumentProps;
  onDocumentUpdated?: () => void;
}

export function DocumentPreviewActions({ document, onDocumentUpdated }: DocumentPreviewActionsProps) {
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);

  const {
    handleReject,
    handleApprove,
    handleSetPrimary,
    rejectNotes,
    setRejectNotes,
    approvalNotes,
    setApprovalNotes,
    isProcessing
  } = useDocumentVerificationHandlers({ 
    document, 
    onDocumentUpdated 
  });

  const canSetPrimary = document.verification_status === 'verified' && !document.primary_document;

  const handleApprovalDialogOpen = () => {
    setApprovalDialogOpen(true);
  };

  const handleRejectionDialogOpen = () => {
    setRejectionDialogOpen(true);
  };

  const handleApprovalConfirm = async () => {
    await handleApprove();
    setApprovalDialogOpen(false);
  };

  const handleRejectionConfirm = async () => {
    await handleReject();
    setRejectionDialogOpen(false);
  };

  const handleSetPrimaryClick = async () => {
    await handleSetPrimary();
  };

  return (
    <>
      <div className="border rounded-lg p-4 w-full">
        <h4 className="text-sm font-medium mb-3">Document Actions</h4>
        <div className="space-y-3 w-full">
          {document.verification_status === "pending" && (
            <div className="flex flex-col sm:flex-row gap-2 w-full">
              <Button 
                onClick={handleApprovalDialogOpen}
                className="bg-green-600 hover:bg-green-700 text-white flex-1 min-w-0"
                disabled={isProcessing}
                size="sm"
              >
                <CheckCircle2 className="h-4 w-4 mr-1 flex-shrink-0" />
                <span className="truncate">Approve</span>
              </Button>
              
              <Button 
                onClick={handleRejectionDialogOpen}
                variant="destructive"
                className="flex-1 min-w-0"
                disabled={isProcessing}
                size="sm"
              >
                <X className="h-4 w-4 mr-1 flex-shrink-0" />
                <span className="truncate">Reject</span>
              </Button>
            </div>
          )}
          
          {canSetPrimary && (
            <Button 
              onClick={handleSetPrimaryClick}
              variant="outline"
              className="w-full min-w-0"
              disabled={isProcessing}
              size="sm"
            >
              <Star className="h-4 w-4 mr-1 flex-shrink-0" />
              <span className="truncate">Set as Primary</span>
            </Button>
          )}
          
          {document.verification_status === "verified" && (
            <div className="text-sm text-green-600 bg-green-50 p-2 rounded break-words">
              ✓ This document has been verified
            </div>
          )}
          
          {document.verification_status === "rejected" && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded break-words">
              ✗ This document has been rejected
            </div>
          )}
        </div>
      </div>

      <DocumentApprovalDialogComponent
        document={document}
        open={approvalDialogOpen}
        onOpenChange={setApprovalDialogOpen}
        approvalNotes={approvalNotes}
        onApprovalNotesChange={setApprovalNotes}
        onApprove={handleApprovalConfirm}
      />

      <DocumentRejectionDialog
        open={rejectionDialogOpen}
        onOpenChange={setRejectionDialogOpen}
        rejectNotes={rejectNotes}
        onRejectNotesChange={setRejectNotes}
        onReject={handleRejectionConfirm}
      />
    </>
  );
}
